import { useCallback, useEffect, useState } from "react";
import Modal from "components/elements/Modal";
import classes from "./classes.module.scss";
import { XMarkIcon } from "@heroicons/react/20/solid";
import Button, { EButtonVariant, EButtonColor } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import Input from "components/materials/Form/InputElement";
import { TextArea } from "components/materials/Form/TextAreaElement";
import VoteOptionInput from "./VoteOptionInput";
import Form, { useForm } from "components/materials/Form";
import { container } from "tsyringe";
import QuestionService from "services/projects/QuestionService";
import QuestionRequestResource from "common/resources/Project/Config/Vote/QuestionRequestResource";
import { useProjectId } from "hooks/useProjectId";
import QuestionResponseResource from "common/resources/Project/Config/Vote/QuestionResponseResource";
import { generateUniqueId } from "utils/UniqueIdUtils";
import ChoiceRequestResource from "common/resources/Project/Config/Choice/ChoiceRequestResource";
import ChoiceService from "services/projects/ChoiceService";
import { formatDateForDateTimeLocal } from "utils/DateUtils";

const questionService = container.resolve(QuestionService);
const choiceService = container.resolve(ChoiceService);

type ChoiceFormInput = {
	id: string;
	label: string;
	image?: File;
};
type IVoteOption = { id: string; value: string; image?: File };
type IFormData = { [key: string]: unknown };
type IProps = {
	vote: QuestionResponseResource;
	onEdited: () => void;
	onClose: () => void;
};

export default function EditVoteModal(props: IProps) {
	const { clearErrors, errors, handleError, formRef } = useForm();
	const { voteOptions, onRemoveOption, onAddOption } = useVoteOptions(props.vote);
	const { editVote } = useEditVote(props.vote, voteOptions);

	const onSubmit = useCallback(
		async (formData: IFormData) => {
			try {
				clearErrors();
				await editVote(formData);
				props.onEdited();
			} catch (error) {
				handleError(error);
			}
		},
		[clearErrors, handleError, editVote, props],
	);

	return (
		<Form onSubmit={onSubmit} errors={errors} formRef={formRef}>
			<Modal isOpen onClose={props.onClose} className={classes["root"]}>
				<div className={classes["header"]}>
					<Typography type="p" size="large" weight="bold">
						{I18n.asset.pages.professional_space.tabs.votes.edit_vote_modal.title}
					</Typography>
					<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
				</div>

				<div className={classes["form-container"]}>
					<TextArea
						defaultValue={props.vote.content}
						name="content"
						label={I18n.asset.pages.professional_space.tabs.votes.edit_vote_modal.vote_description}
						className={classes["input"]}
						required
					/>
					<Input
						name="endDate"
						type="datetime-local"
						defaultValue={props.vote.endsAt && formatDateForDateTimeLocal(props.vote.endsAt)}
						label={I18n.asset.pages.professional_space.tabs.votes.edit_vote_modal.vote_end_date}
						className={classes["input"]}
						required
					/>
					<div className={classes["options-container"]}>
						<Typography type="p" size="small" weight="medium">
							{I18n.asset.pages.professional_space.tabs.votes.edit_vote_modal.vote_options}
						</Typography>
						<div className={classes["options"]}>
							{voteOptions.map((option, index) => (
								<VoteOptionInput
									key={option.id}
									id={option.id}
									index={index}
									onRemove={onRemoveOption}
									defaultValue={option.value}
									defaultFile={option.image}
									formRef={formRef}
								/>
							))}
						</div>
					</div>
					<Button variant={EButtonVariant.OUTLINED} color={EButtonColor.PRIMARY} onClick={onAddOption}>
						{I18n.asset.pages.professional_space.tabs.votes.edit_vote_modal.vote_add_option}
					</Button>
					{errors.length > 0 && (
						<Typography type="p" size="small" weight="medium" color="error">
							{I18n.asset.pages.professional_space.tabs.votes.edit_vote_modal.vote_option_max_length_error}
						</Typography>
					)}
				</div>

				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onClose}>
						{I18n.asset.pages.professional_space.tabs.votes.edit_vote_modal.cancel}
					</Button>
					<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} type="submit">
						{I18n.asset.pages.professional_space.tabs.votes.edit_vote_modal.create_vote}
					</Button>
				</div>
			</Modal>
		</Form>
	);
}

export const useEditVote = (vote: QuestionResponseResource, voteOptions: IVoteOption[]) => {
	const { projectId } = useProjectId();

	const buildQuestionResource = useCallback(
		(formData: IFormData): QuestionRequestResource => {
			return QuestionRequestResource.hydrate<QuestionRequestResource>({
				content: formData["content"] as string,
				category: vote.category,
				endsAt: new Date(formData["endDate"] as string),
			});
		},
		[vote],
	);

	const extractChoicesFromForm = useCallback((formData: IFormData, voteOptions: IVoteOption[]): ChoiceFormInput[] => {
		return voteOptions
			.map((option) => {
				const label = formData[`option-${option.id}`] as string;
				const image = (formData[`option-image-${option.id}`] as FileList)?.[0];
				return label ? { id: option.id, label, image } : null;
			})
			.filter(Boolean) as ChoiceFormInput[];
	}, []);

	const splitChoices = useCallback((formChoices: ChoiceFormInput[], vote: QuestionResponseResource) => {
		const existingIds = vote.choices.map((c) => c.id);

		const toUpdate = formChoices.filter((c) => existingIds.includes(c.id));
		const toCreate = formChoices.filter((c) => !existingIds.includes(c.id));
		const toDelete = vote.choices.filter((c) => !formChoices.some((fc) => fc.id === c.id));

		return { toCreate, toUpdate, toDelete };
	}, []);

	const hydrateAndValidateChoices = useCallback(async (choices: ChoiceFormInput[]) => {
		const hydrated = ChoiceRequestResource.hydrateArray<ChoiceRequestResource>(choices);
		await Promise.all(hydrated.map((c) => c.validateOrReject()));
		return hydrated;
	}, []);

	const editVote = useCallback(
		async (formData: IFormData) => {
			const questionResource = buildQuestionResource(formData);
			const formChoices = extractChoicesFromForm(formData, voteOptions);

			const { toCreate, toUpdate, toDelete } = splitChoices(formChoices, vote);

			await questionResource.validateOrReject();
			const [hydratedToCreate, hydratedToUpdate] = await Promise.all([hydrateAndValidateChoices(toCreate), hydrateAndValidateChoices(toUpdate)]);

			await questionService.update(projectId, vote.id, questionResource);

			await Promise.all(
				hydratedToUpdate.map((choice, i) => {
					const raw = toUpdate[i];
					if (!raw) throw new Error("Raw choice not found");
					return choiceService.update(projectId, vote.id, raw.id, choice, raw.image);
				}),
			);

			await Promise.all(
				hydratedToCreate.map((choice, i) => {
					const raw = toCreate[i];
					if (!raw) throw new Error("Raw choice not found");
					return choiceService.create(projectId, vote.id, choice, raw.image);
				}),
			);

			await Promise.all(toDelete.map((choice) => choiceService.delete(projectId, vote.id, choice.id)));
		},
		[projectId, vote, voteOptions, buildQuestionResource, extractChoicesFromForm, splitChoices, hydrateAndValidateChoices],
	);

	return { editVote };
};
const useVoteOptions = (vote: QuestionResponseResource) => {
	const { projectId } = useProjectId();
	const [voteOptions, setVoteOptions] = useState<IVoteOption[]>(vote.choices.map((choice) => ({ id: choice.id, value: choice.value })));

	const onAddOption = useCallback(() => {
		setVoteOptions((prev) => [...prev, { id: generateUniqueId(), value: "" }]);
	}, []);

	const onRemoveOption = useCallback((id: string) => {
		setVoteOptions((prev) => prev.filter((option) => option.id !== id));
	}, []);

	const fetchChoiceFile = useCallback(
		async (choiceId: string, questionId: string, fileName: string, mimeType: string) => {
			const blob = await choiceService.downloadImage(projectId, questionId, choiceId);
			return new File([blob], fileName, { type: mimeType });
		},
		[projectId],
	);

	const loadChoiceImages = useCallback(async () => {
		const choicesWithImages = vote.choices.filter((choice) => choice.image);

		const updatedOptions = await Promise.all(
			choicesWithImages.map(async (choice) => {
				if (!choice.image) throw new Error("Choice image is missing");
				const file = await fetchChoiceFile(choice.id, vote.id, choice.image.name, choice.image.mimeType);
				return { id: choice.id, value: choice.value, image: file };
			}),
		);

		setVoteOptions((prev) =>
			prev.map((option) => {
				const updated = updatedOptions.find((opt) => opt.id === option.id);
				return updated ? { ...option, image: updated.image } : option;
			}),
		);
	}, [vote.choices, vote.id, fetchChoiceFile]);

	useEffect(() => {
		loadChoiceImages();
	}, [loadChoiceImages]);

	return { voteOptions, onAddOption, onRemoveOption };
};
